import httpWrapper from '../HttpWrapper'
import { AddCandidateToRoleReq } from './models/addCandidateToRole/AddCandidateToRoleReq'
import { AddCompanyLinksReq } from './models/addCompanyLinks/AddCompanyLinksReq'
import { AddHiringTeamMemberReq } from './models/addHiringTeamMember/AddHiringTeamMemberReq'
import { AddHiringTeamMembersReq } from './models/addHiringTeamMembers/AddHiringTeamMembersReq'
import { AddNetworkLinksReq } from './models/addNetworkLinks/AddNetworkLinksReq'
import { CreateRoleReq } from './models/createRole/CreateRoleReq'
import { CreateRoleRes } from './models/createRole/CreateRoleRes'
import { DeleteCompanyLinksReq } from './models/deleteCompanyLinks/DeleteCompanyLinksReq'
import { DeleteHiringTeamMemberReq } from './models/deleteHiringTeamMember/DeleteHiringTeamMemberReq'
import { DeleteNetworkLinksReq } from './models/deleteNetworkLinks/DeleteNetworkLinksReq'
import { GetRoleReq } from './models/get/GetRoleReq'
import { GetRes } from './models/get/GetRoleRes'
import { GetAllRes } from './models/getAll/GetAllRolesRes'
import { GetAllRolesLiteReq } from './models/getAllLite/GetAllRolesLiteReq'
import { GetAllRolesLiteRes } from './models/getAllLite/GetAllRolesLiteRes'
import { GetCandidatesReq } from './models/getCandidates/GetCandidatesReq'
import { GetCandidatesRes } from './models/getCandidates/GetCandidatesRes'
import { GetJobRelevancyCandidatesReq } from './models/getJobRelevancyCandidates/GetJobRelevancyCandidatesReq'
import { GetJobRelevancyCandidatesRes } from './models/getJobRelevancyCandidates/GetJobRelevancyCandidatesRes'
import { GetRoleLiteReq } from './models/getLite/GetRoleLiteReq'
import { GetRoleLiteRes } from './models/getLite/GetRoleLiteRes'
import { GetRankingResultsReq } from './models/getRankingResults/GetRankingResultsReq'
import { GetRankingResultsRes } from './models/getRankingResults/GetRankingResultsRes'
import { ProcessRoleReq } from './models/processRole/ProcessRoleReq'
import { RemoveCandidateFromRoleReq } from './models/removeCandidateFromRole/RemoveCandidateFromRoleReq'
import { SaveHiringManagerFeedbackForCandidateReq } from './models/saveHiringManagerFeedback/SaveHiringManagerFeedbackReq'
import { SearchCandidatesRes } from './models/searchCandidates/SearchCandidatesRes'
import { SearchOnJobRelevancyRes } from './models/searchOnJobRelevancy/SearchOnJobRelevancyRes'
import { SetCandidateContactedValueReq } from './models/setCandidateContactedValue/SetCandidateContactedValueReq'
import { SetCandidateInterestedValueReq } from './models/setCandidateInterestedValue/SetCandidateInterestedValueReq'
import { SetCandidatePresentValueReq } from './models/setCandidatePresentValue/SetCandidatePresentValueReq'
import { UpdateRoleReq } from './models/updateRole/UpdateRoleReq'

class RoleApi {
    private static instance: RoleApi

    public constructor() {
        if (RoleApi.instance) {
            return RoleApi.instance
        }

        RoleApi.instance = this
    }

    public async getAll(): Promise<GetAllRes> {
        return await httpWrapper.get('/role/all')
    }

    public async get(data: GetRoleReq): Promise<GetRes> {
        const queryParams = new URLSearchParams({
            roleId: data.roleId,
        })

        if (data.searchTerm) {
            queryParams.append('searchTerm', data.searchTerm)
        }

        if (data.hiringManagerId) {
            queryParams.append('hiringManagerId', data.hiringManagerId)
        }

        if (data.returnExcludedCandidates) {
            queryParams.append(
                'returnExcludedCandidates',
                data.returnExcludedCandidates.toString(),
            )
        }

        queryParams.append('rankingModel', data.rankingModel)

        return await httpWrapper.get(`/role`, queryParams)
    }

    public async getLite(data: GetRoleLiteReq): Promise<GetRoleLiteRes> {
        const queryParams = new URLSearchParams({
            roleId: data.roleId,
        })

        if (data.includeHiringTeamIds !== undefined) {
            queryParams.append(
                'includeHiringTeamIds',
                data.includeHiringTeamIds.toString(),
            )
        }

        if (data.includeNetworkLinkIds !== undefined) {
            queryParams.append(
                'includeNetworkLinkIds',
                data.includeNetworkLinkIds.toString(),
            )
        }

        if (data.includeCompanyLinkIds !== undefined) {
            queryParams.append(
                'includeCompanyLinkIds',
                data.includeCompanyLinkIds.toString(),
            )
        }

        return await httpWrapper.get(`/role/lite`, queryParams)
    }

    public async getAllLite(
        data: GetAllRolesLiteReq,
    ): Promise<GetAllRolesLiteRes> {
        const queryParams = new URLSearchParams()

        if (data.includeHiringTeamIds !== undefined) {
            queryParams.append(
                'includeHiringTeamIds',
                data.includeHiringTeamIds.toString(),
            )
        }

        return await httpWrapper.get(`/role/all-lite`, queryParams)
    }

    public async setCandidatePresentValue(
        data: SetCandidatePresentValueReq,
    ): Promise<void> {
        return await httpWrapper.post('/role/set-candidate-present-value', data)
    }

    public async setCandidateContactedValue(
        data: SetCandidateContactedValueReq,
    ): Promise<void> {
        return await httpWrapper.post(
            '/role/set-candidate-contacted-value',
            data,
        )
    }

    public async setCandidateInterestedValue(
        data: SetCandidateInterestedValueReq,
    ): Promise<void> {
        return await httpWrapper.post(
            '/role/set-candidate-interested-value',
            data,
        )
    }

    public async addHiringTeamMember(
        data: AddHiringTeamMemberReq,
    ): Promise<void> {
        return await httpWrapper.post('/role/add-hiring-team-member', data)
    }

    public async addHiringTeamMembers(
        data: AddHiringTeamMembersReq,
    ): Promise<void> {
        return await httpWrapper.post('/role/add-hiring-team-members', data)
    }

    public async deleteHiringTeamMember(
        data: DeleteHiringTeamMemberReq,
    ): Promise<void> {
        return await httpWrapper.post('/role/delete-hiring-team-member', data)
    }

    public async addNetworkLinks(data: AddNetworkLinksReq): Promise<void> {
        return await httpWrapper.post('/role/add-network-links', data)
    }

    public async deleteNetworkLinks(
        data: DeleteNetworkLinksReq,
    ): Promise<void> {
        return await httpWrapper.post('/role/delete-network-links', data)
    }

    public async addCompanyLinks(data: AddCompanyLinksReq): Promise<void> {
        return await httpWrapper.post('/role/add-company-links', data)
    }

    public async deleteCompanyLinks(
        data: DeleteCompanyLinksReq,
    ): Promise<void> {
        return await httpWrapper.post('/role/delete-company-links', data)
    }

    // public async saveHiringManagerFeedback(
    //     data: SaveHiringManagerFeedbackReq,
    // ): Promise<void> {
    //     return await httpWrapper.post(
    //         '/role/save-hiring-manager-feedback-for-candidate',
    //         data,
    //     )
    // }

    public async saveHiringManagerFeedbackForCandidate(
        data: SaveHiringManagerFeedbackForCandidateReq,
    ): Promise<void> {
        return await httpWrapper.post(
            '/role/save-hiring-manager-feedback-for-candidate',
            data,
        )
    }

    public async createRole(data: CreateRoleReq): Promise<CreateRoleRes> {
        return await httpWrapper.post('/role/create-role', data)
    }

    public async sendRequestFeedbackEmails(roleId: string): Promise<void> {
        await httpWrapper.post(`/role/send-request-feedback-emails`, { roleId })
    }

    public async updateRole(data: UpdateRoleReq): Promise<void> {
        await httpWrapper.post('/role/update-role', data)
    }

    public async processRole(data: ProcessRoleReq): Promise<void> {
        await httpWrapper.post('/role/process-role', data)
    }

    public async getCandidates(
        data: GetCandidatesReq,
    ): Promise<GetCandidatesRes> {
        return await httpWrapper.post('/role/candidates-lite', data)
    }

    public async getJobRelevancyCandidates(
        data: GetJobRelevancyCandidatesReq,
    ): Promise<GetJobRelevancyCandidatesRes> {
        return await httpWrapper.post(
            '/ranking/get-job-relevancy-candidates',
            data,
        )
    }

    public async getRankingResults(
        data: GetRankingResultsReq,
    ): Promise<GetRankingResultsRes> {
        return await httpWrapper.post('/ranking/get-ranking-results', data)
    }

    public async exportCandidatesCsv(roleId: string): Promise<Blob> {
        const queryParams = new URLSearchParams({
            roleId: roleId,
        })

        return await httpWrapper.get<Blob>(
            `/role/export-candidates-csv`,
            queryParams,
            { responseType: 'blob' },
        )
    }

    public async searchCandidates(
        searchPhrases: string[],
    ): Promise<SearchCandidatesRes> {
        return await httpWrapper.post('/ranking/search-candidates', {
            searchPhrases,
        })
    }

    public async searchOnJobRelevancy(
        searchPhrases: string[],
        jobTitles: string[],
    ): Promise<SearchOnJobRelevancyRes> {
        return await httpWrapper.post('/ranking/search-on-job-relevancy', {
            searchPhrases,
            jobTitles,
        })
    }

    public async inviteCandidateToApply(
        roleId: string,
        candidateId: string,
    ): Promise<void> {
        return await httpWrapper.post('/role/invite-candidate-to-apply', {
            roleId,
            candidateId,
        })
    }

    public async bookInterview(
        roleId: string,
        candidateId: string,
        hmAvailableDateTimes: Date[],
    ): Promise<void> {
        return await httpWrapper.post('/role/book-interview', {
            roleId,
            candidateId,
            hmAvailableDateTimes,
        })
    }

    public async addCandidateToRole(
        data: AddCandidateToRoleReq,
    ): Promise<void> {
        return await httpWrapper.post('/role/add-candidate-to-role', data)
    }

    public async removeCandidateFromRole(
        data: RemoveCandidateFromRoleReq,
    ): Promise<void> {
        return await httpWrapper.post('/role/remove-candidate-from-role', data)
    }
}

const roleApi = new RoleApi()
export default roleApi
